/**
 * The game's side of the sound effects: the ctx that places the event mapping's sounds in the world
 * (sfx/events.ts), and what sounds every frame without an event — footsteps in step with the walk
 * frames, the loops (meditation, the flying sword, gliding, a training spot's ambience, a warp
 * circle's hum) and the ticks scattered over them (drips, gusts, lanterns at night, the temple gong
 * at dawn and dusk, an eagle's wings), and the stars of a stun.
 *
 * main.ts calls `withEventSfx` for each drained event and `tickSfx` once a frame.
 */
import {
  plainsLayout,
  rankFor,
  spotsIn,
  Surface,
  surfKind,
  Tile,
  tileAt,
  type Entity,
  type TrainingSpotDef,
  type WorldEvent,
} from '@nine/shared';
import type { Effects } from '../render/effects';
import { nightAt } from '../render/lighting';
import { strideFrame } from '../render/qinggongAnim';
import { fxWeaponOf } from '../render/skillFx';
import { FRAMES } from '../render/sprites';
import { tickDuelSfx } from './duelWatch';
import { sfxEngine, type SfxHandle } from './engine';
import { playEventSfx, rankSeen, type EventSfxCtx } from './events';
import type { SfxOpts } from './registry';
import type { SwingWeapon } from './sounds/combat';
import type { Ground } from './sounds/movement';
import { SPOT_TICK } from './sounds/world';
import { placeAt } from './spatial';

// ───────── the event ctx ─────────

/** Sounds held back to a world time (a dance's beats). */
interface Later {
  t: number;
  id: string;
  ent: number | null;
  x: number;
  y: number;
  o: SfxOpts;
}
const later: Later[] = [];

/** Effects' weapon families, by the four that swing differently. */
const WEAPON_CLASS: Record<string, SwingWeapon | 'claw'> = {
  jian: 'sword',
  fan: 'sword',
  dao: 'sabre',
  polearm: 'sabre',
  spear: 'staff',
  staff: 'staff',
  fist: 'fist',
  sleeve: 'fist',
  claw: 'claw',
};

let gameCtx: (EventSfxCtx & { fx: Effects }) | null = null;

/** A sound's place: none for the player's own (centred, full), pan and distance for the rest. */
function placeOf(fx: Effects, at: Entity | { x: number; y: number } | null | undefined): SfxOpts {
  return !at || at === fx.world.player ? {} : placeAt(fx, at.x, at.y);
}

/** The ctx that plays the event mapping into the game, placed by the camera of `fx`. */
export function ctxFor(fx: Effects): EventSfxCtx {
  if (gameCtx?.fx === fx) return gameCtx;
  const weapon = (e: Entity): SwingWeapon | 'claw' => {
    const set = fx.sprites.of(e);
    if (!set?.poses.cut) return e.kind === 'player' ? 'fist' : 'claw';
    // A figure without a weapon in hand fights with palms (monsters always strike armed).
    if (!set.armed && e.kind !== 'monster') return 'fist';
    return WEAPON_CLASS[fxWeaponOf(e, true)] ?? 'sword';
  };
  gameCtx = {
    fx,
    get world() {
      return fx.world;
    },
    weapon,
    play(id, at, o = {}) {
      if (o.delay && o.delay > 0.03) {
        const p = at ?? fx.world.player;
        later.push({
          t: fx.world.time + o.delay,
          id,
          ent: at && 'id' in at ? at.id : null,
          x: p.x,
          y: p.y,
          o: { ...o, delay: 0 },
        });
        return;
      }
      sfxEngine.play(id, { ...o, ...placeOf(fx, at) });
    },
  };
  return gameCtx;
}

/**
 * Plays an event's sounds, then runs its visuals (`run`: Effects.handle) with the old effect calls
 * silenced when a rule spoke for the event.
 */
export function withEventSfx(ev: WorldEvent, fx: Effects, run: () => void): void {
  sfxEngine.claimed = playEventSfx(ev, ctxFor(fx));
  try {
    run();
  } finally {
    sfxEngine.claimed = false;
  }
}

function flushLater(fx: Effects): void {
  const now = fx.world.time;
  for (let i = later.length - 1; i >= 0; i--) {
    const l = later[i]!;
    if (l.t > now) continue;
    later.splice(i, 1);
    // Long overdue (a paused tab): dropped rather than played in a heap.
    if (now - l.t > 0.25) continue;
    const e = l.ent !== null ? fx.world.get(l.ent) : undefined;
    sfxEngine.play(l.id, { ...l.o, ...placeOf(fx, e ?? { x: l.x, y: l.y }) });
  }
  if (later.length > 64) later.splice(0, later.length - 64);
}

// ───────── every frame ─────────

/** Footsteps are heard from this many tiles, from the player and at most this many others. */
const STEP_RANGE = 9;
const STEP_OTHERS = 4;
/** Stuns are heard from this far. */
const STUN_RANGE = 14;
/** A training spot's bed is heard this many tiles beyond its circle, a warp circle's hum within this. */
const SPOT_REACH = 9;
const PORTAL_REACH = 7;

const strides = new Map<number, number>();
const stunned = new Set<number>();
const loops = new Map<string, SfxHandle>();
let spotTickT = 1.5;
let lanternT = 3;
let flapT = 0;
let birdT = 4;
let cricketT = 2;
let frogT = 5;
let brazierT = 1;
let bannerT = 3;
let lastHour = -1;
let rng = 0x2545f491;

/** A cheap random for the ticks' timing (not the simulation's: it never touches the World). */
function rand(): number {
  rng ^= rng << 13;
  rng ^= rng >>> 17;
  rng ^= rng << 5;
  return (rng >>> 0) / 4294967296;
}

/** The ground under a point: what a foot falling there sounds like. */
export function groundOf(fx: Effects, x: number, y: number): Ground {
  const w = fx.world;
  if (w.interiors.inside) return 'wood';
  const t = tileAt(w.map, Math.floor(x), Math.floor(y));
  if (t === Tile.Water || t === Tile.Paddy) return 'water';
  if (t === Tile.Pier || t === Tile.Building) return 'wood';
  if (t === Tile.Paved || t === Tile.Terrace || t === Tile.Stele || t === Tile.Rock) return 'stone';
  if (w.map.zone === 'snow') return 'snow';
  // Open ground: what it is surfaced with (the map's look layer).
  switch (surfKind(w.map, Math.floor(x), Math.floor(y))) {
    case Surface.Earth:
    case Surface.Plots:
      return 'earth';
    case Surface.Stone:
    case Surface.Paving:
    case Surface.Flagstones:
      return 'stone';
    case Surface.Sand:
      return 'sand';
    case Surface.Deck:
      return 'wood';
    case Surface.Snow:
      return 'snow';
    case Surface.Gravel:
      return 'gravel';
    case Surface.Moss:
      return 'moss';
    case Surface.Paddy:
      return 'water';
    default:
      return 'grass';
  }
}

/** The nearest placed plains prop (shared layout) passing `pick`, within `reach` tiles. */
function nearestProp(x: number, y: number, pick: (id: string) => boolean, reach: number): readonly [number, number] | null {
  let best: readonly [number, number] | null = null;
  let bd = reach;
  for (const q of plainsLayout.PLAINS_PROPS) {
    if (!pick(q.id)) continue;
    const d = Math.hypot(q.at[0] - x, q.at[1] - y);
    if (d < bd) {
      bd = d;
      best = q.at;
    }
  }
  return best;
}

/** Keeps a loop sounding (at a level and pan) while wanted, and fades it out once not. */
function keep(key: string, id: string, want: boolean, level = 1, pan = 0): void {
  let h = loops.get(key);
  if (want && level > 0.02) {
    if (!h?.alive) {
      h = sfxEngine.play(id, { intensity: level, pan }) ?? undefined;
      if (h) loops.set(key, h);
    } else {
      h.setLevel(level, 0.25);
      h.setPan(pan, 0.25);
    }
  } else if (h) {
    h.stop(0.8);
    loops.delete(key);
  }
}

/** Stops every loop (leaving the world for the front screens). */
export function stopSfxLoops(): void {
  for (const h of loops.values()) h.stop(0.5);
  loops.clear();
}

/** The training spot nearest the player in this zone, and how near (0 far … 1 inside). */
function nearSpot(fx: Effects): { spot: TrainingSpotDef; near: number } | null {
  const w = fx.world;
  if (w.interiors.inside) return null;
  const p = w.player;
  let best: { spot: TrainingSpotDef; near: number } | null = null;
  for (const s of spotsIn(w.map.zone)) {
    const d = Math.hypot(p.x - s.area.x, p.y - s.area.y) - s.area.r;
    const near = d <= 0 ? 1 : Math.max(0, 1 - d / SPOT_REACH);
    if (near > (best?.near ?? 0)) best = { spot: s, near };
  }
  return best;
}

/** Seconds between the ticks over each kind of spot's bed. */
const TICK_GAP: Record<string, [number, number]> = {
  'amb.splash': [0.5, 1.4],
  'amb.drip': [0.9, 2.6],
  'amb.gust': [3, 7],
  'amb.knock': [1.5, 4.5],
  'amb.bubble': [0.4, 1.3],
  'amb.chime': [3, 8],
  'amb.wave': [4, 7],
};

/**
 * Everything that sounds without an event, once a frame. `started`: in the world (not behind the
 * front screens).
 */
export function tickSfx(dt: number, fx: Effects, started: boolean): void {
  if (!started) {
    if (loops.size) stopSfxLoops();
    return;
  }
  if (!sfxEngine.ctx) return;
  const w = fx.world;
  const p = w.player;
  flushLater(fx);
  tickDuelSfx(fx);
  rankSeen.index = rankFor(w.progress.cultivationSpent).index;

  // ── footsteps (on the walk frames' footfalls) and stuns ──
  const near: { e: Entity; d: number }[] = [];
  for (const e of w.entities.values()) {
    const d = Math.hypot(e.x - p.x, e.y - p.y);
    if (e.stunT > 0 && !e.dead) {
      if (!stunned.has(e.id) && d < STUN_RANGE) {
        stunned.add(e.id);
        sfxEngine.play('combat.stun', e === p ? {} : placeAt(fx, e.x, e.y));
      }
    } else stunned.delete(e.id);
    if (e !== p && e.moving && !e.dead && d < STEP_RANGE) near.push({ e, d });
  }
  near.sort((a, b) => a.d - b.d);
  const walkers = [p, ...near.slice(0, STEP_OTHERS).map((n) => n.e)];
  const seen = new Set<number>();
  for (const e of walkers) {
    seen.add(e.id);
    const set = fx.sprites.of(e);
    if (!set?.poses.cut || !e.moving || e.mount || e.z > 0.5 || e.dead) {
      strides.delete(e.id);
      continue;
    }
    const f = strideFrame(e, set);
    const last = strides.get(e.id);
    strides.set(e.id, f);
    if (last === undefined || last === f) continue;
    // A foot falls on the first frame of each half of the stride.
    const n = (f - last + FRAMES) % FRAMES;
    let fall = false;
    for (let k = 1; k <= n; k++) if ((last + k) % (FRAMES / 2) === 0) fall = true;
    if (!fall) continue;
    const water = e === p && w.qinggong.onWater;
    const id = water ? 'move.waterRun' : `move.step.${groundOf(fx, e.x, e.y)}`;
    const o: SfxOpts =
      e === p ? { intensity: e.sprinting ? 1 : 0.8 } : { intensity: 0.7, ...placeAt(fx, e.x, e.y) };
    sfxEngine.play(id, o);
  }
  for (const id of strides.keys()) if (!seen.has(id)) strides.delete(id);

  // ── loops ──
  keep('meditate', 'world.meditate.hum', p.meditating);
  keep('sword', 'move.swordFlight', p.mount === 'flying-sword', p.moving ? 1 : 0.6);
  keep('glide', 'move.glide', w.qinggong.gliding && !p.mount);
  const spot = nearSpot(fx);
  for (const key of loops.keys())
    if (key.startsWith('spot:') && key !== `spot:${spot?.spot.id}`) keep(key, '', false);
  if (spot) {
    const pan = placeAt(fx, spot.spot.area.x, spot.spot.area.y).pan ?? 0;
    keep(
      `spot:${spot.spot.id}`,
      `amb.${spot.spot.kind}`,
      true,
      spot.near * spot.near,
      pan * (1 - spot.near * 0.7),
    );
    if ((spotTickT -= dt) <= 0 && spot.near > 0.25) {
      const tick = SPOT_TICK[spot.spot.kind];
      const [a, b] = TICK_GAP[tick] ?? [2, 5];
      spotTickT = a + rand() * (b - a);
      const ang = rand() * Math.PI * 2;
      const r = spot.spot.area.r * rand();
      const at = placeAt(fx, spot.spot.area.x + Math.cos(ang) * r, spot.spot.area.y + Math.sin(ang) * r);
      sfxEngine.play(tick, { intensity: spot.near, pan: at.pan });
    }
  }
  let portal = 0;
  let portalPan = 0;
  if (!w.interiors.inside)
    for (const g of w.map.portals) {
      const d = Math.hypot(g.x - p.x, g.y - p.y);
      const near2 = Math.max(0, 1 - d / PORTAL_REACH);
      if (near2 > portal) {
        portal = near2;
        portalPan = placeAt(fx, g.x, g.y).pan ?? 0;
      }
    }
  keep('portal', 'world.warp.hum', portal > 0, portal, portalPan);

  // ── the outdoors by time of day: a breeze under everything, birds by day, crickets and frogs at night ──
  const outdoors = !w.interiors.inside && !p.meditating;
  const dark = nightAt(w.hour).dark;
  const spotNear = spot?.near ?? 0;
  keep('breeze', 'amb.breeze', outdoors, (0.7 - dark * 0.3) * (1 - spotNear * 0.8));
  if (outdoors && spotNear < 0.5) {
    if (dark < 0.35 && (birdT -= dt) <= 0) {
      birdT = 3 + rand() * 7;
      sfxEngine.play('amb.bird', { intensity: 0.6 + rand() * 0.4, pan: rand() * 1.6 - 0.8, seed: (rand() * 1e9) | 0 });
    }
    if (dark > 0.5 && (cricketT -= dt) <= 0) {
      cricketT = 0.8 + rand() * 2.5;
      sfxEngine.play('amb.cricket', { intensity: 0.5 + rand() * 0.5, pan: rand() * 1.8 - 0.9 });
    }
    if (dark > 0.4 && (frogT -= dt) <= 0) {
      frogT = 2.5 + rand() * 5;
      // Frogs only where there is water close by.
      const ang = rand() * Math.PI * 2;
      const fx2 = p.x + Math.cos(ang) * 5;
      const fy2 = p.y + Math.sin(ang) * 5;
      const t = tileAt(w.map, Math.floor(fx2), Math.floor(fy2));
      if (t === Tile.Water || t === Tile.Paddy) sfxEngine.play('amb.frog', placeAt(fx, fx2, fy2));
    }
  }

  // ── the plains kit's props (lane C0-PROPS): braziers crackle and banners flap near the player ──
  if (outdoors && w.map.zone === 'plains' && w.map.w === 256) {
    if ((brazierT -= dt) <= 0) {
      brazierT = 1 + rand() * 1.2;
      const b = nearestProp(p.x, p.y, (id) => id === 'brazier', 9);
      if (b) sfxEngine.play('world.brazier', { ...placeAt(fx, b[0], b[1]), intensity: 0.8 + dark * 0.4 });
    }
    if ((bannerT -= dt) <= 0) {
      bannerT = 2.5 + rand() * 4;
      const b = nearestProp(p.x, p.y, (id) => id.startsWith('banner') || id.startsWith('pennant'), 8);
      if (b) sfxEngine.play('world.banner.flap', placeAt(fx, b[0], b[1]));
    }
  }

  // ── ticks ──
  if (p.mount === 'eagle' && (flapT -= dt) <= 0) {
    flapT = p.moving ? 0.55 + rand() * 0.15 : 1.3 + rand() * 0.4;
    sfxEngine.play('move.eagle.flap', { intensity: p.moving ? 1 : 0.6 });
  }
  if (nightAt(w.hour).dark > 0.5 && (lanternT -= dt) <= 0) {
    lanternT = 2.5 + rand() * 3;
    let best: Entity | null = null;
    let bd = 8;
    for (const e of w.entities.values()) {
      if (!e.folk?.lantern) continue;
      const d = Math.hypot(e.x - p.x, e.y - p.y);
      if (d < bd) {
        bd = d;
        best = e;
      }
    }
    if (best) sfxEngine.play('world.lantern', placeAt(fx, best.x, best.y));
  }
  // The temple gong at dawn and dusk, from somewhere across the zone.
  const hour = w.hour;
  if (lastHour >= 0 && !w.interiors.inside)
    for (const h of [6, 18])
      if (lastHour < h && hour >= h && hour - lastHour < 1)
        sfxEngine.play('world.gong', { dist: 9, pan: -0.35 });
  lastHour = hour;
}
